import { useContext, useRef, useState, useEffect } from "react";
import { AuthContext } from "./AuthContext";

export function useAuth() {
  return useContext(AuthContext)?.auth;
}

export function useConfig() {
  return useContext(AuthContext)?.config;
}

export function useUser() {
  const auth = useContext(AuthContext)?.auth;
  return authInfo(auth).user;
}

export function useAuthInfo() {
  const auth = useContext(AuthContext)?.auth;
  return authInfo(auth);
}

function authInfo(auth: any) {
  // Be defensive: incoming auth may be undefined or partially populated
  const status = auth?.status;
  const meta = auth?.meta;

  const isAuthenticated =
    status === 200 || (status === 401 && !!meta?.is_authenticated);
  const requiresReauthentication = isAuthenticated && status === 401;
  const pendingFlow = auth?.data?.flows?.find((flow: any) => !!flow?.is_pending);

  // Prefer nested data.user; fall back to top-level user if present
  const user = isAuthenticated ? auth?.data?.user ?? auth?.user ?? null : null;

  return {
    isAuthenticated,
    requiresReauthentication,
    user,
    pendingFlow,
  };
}

export const AuthChangeEvent = Object.freeze({
  LOGGED_OUT: "LOGGED_OUT",
  LOGGED_IN: "LOGGED_IN",
  FLOW_UPDATED: "FLOW_UPDATED",
});

function determineAuthChangeEvent(fromAuth: any, toAuth: any) {
  let fromInfo: any = authInfo(fromAuth);
  const toInfo = authInfo(toAuth);
  if (toAuth.status === 410) {
    return AuthChangeEvent.LOGGED_OUT;
  }
  // Corner case: user ID change. Treat as if we're transitioning from anonymous state.
  if (fromInfo.user && toInfo.user && fromInfo.user?.id !== toInfo.user?.id) {
    fromInfo = {
      isAuthenticated: false,
      requiresReauthentication: false,
      user: null,
    };
  }
  if (!fromInfo.isAuthenticated && toInfo.isAuthenticated) {
    // You typically don't transition from logged out to reauthentication required.
    return AuthChangeEvent.LOGGED_IN;
  } else if (fromInfo.isAuthenticated && !toInfo.isAuthenticated) {
    return AuthChangeEvent.LOGGED_OUT;
  } else if (!fromInfo.isAuthenticated && !toInfo.isAuthenticated) {
    const fromFlow = fromInfo.pendingFlow;
    const toFlow = toInfo.pendingFlow;
    if (toFlow?.id && fromFlow?.id !== toFlow.id) {
      return AuthChangeEvent.FLOW_UPDATED;
    }
  }
  // No change.
  return null;
}

export function useAuthChange() {
  const auth = useAuth();
  const ref: any = useRef({ prevAuth: auth, event: null, didChange: false });
  const [, setForcedUpdate] = useState(0);
  useEffect(() => {
    if (ref.current.prevAuth) {
      ref.current.didChange = true;
      const event = determineAuthChangeEvent(ref.current.prevAuth, auth);
      if (event) {
        ref.current.event = event;
        setForcedUpdate((gen) => gen + 1);
      }
    }
    ref.current.prevAuth = auth;
  }, [auth, ref]);
  const didChange = ref.current.didChange;
  if (didChange) {
    ref.current.didChange = false;
  }
  const event = ref.current.event;
  if (event) {
    ref.current.event = null;
  }

  return [auth, event];
}

export function useAuthStatus() {
  const auth = useAuth();
  return [auth, authInfo(auth)];
}
